import './tooltip.scss';

import AzimuthAndDistanceTooltip from '@components/map/tooltips/AzimuthAndDistanceTooltip';
import LabelTooltip from '@components/map/tooltips/LabelTooltip';
import PerimeterPortalLabelTooltip from '@components/map/tooltips/PerimeterPortalLabelTooltip';
import PermanentTooltips from '@components/map/tooltips/PermanentTooltips';
import { TooltipComponentProps, TooltipTypeEnum } from '@components/map/tooltips/tooltip.types';
import { useMapTooltip } from '@hooks/useMapTooltip';
import classNames from 'classnames';
import { FC } from 'react';
import { Popup } from 'react-map-gl/mapbox';

import { Coordinates } from '@/types/commons/commons.types';
import { LayerNameEnum } from '@/types/map.types';

import { useMapContext } from '../MapContext';
import EventTooltip from './EventTooltip';
import NoFlyZoneTooltip from './NoFlyZoneTooltip';
import PoiTooltip from './PoiTooltip';
import StatisticPointTooltip from './StatisticPointTooltips';
import TargetTooltip from './TargetTooltip';

const TOOLTIP_LAYERS = [
  LayerNameEnum.TARGETS,
  LayerNameEnum.EVENTS,
  LayerNameEnum.SENSOR_LABELS,
  LayerNameEnum.PERIMETER_SENSOR_LABELS,
  LayerNameEnum.AUTOMATON_SECTION_LABELS,
  LayerNameEnum.PERIMETER_PORTALS,
  LayerNameEnum.NO_FLY_ZONE_FILLS,
  LayerNameEnum.POI,
  LayerNameEnum.STATISTICS_POINTS,
];

const tooltipComponents: { [K in TooltipTypeEnum]: FC<TooltipComponentProps<K>> } = {
  [TooltipTypeEnum.AZIMUTH_AND_DISTANCE]: AzimuthAndDistanceTooltip,
  [TooltipTypeEnum.EVENT]: EventTooltip,
  [TooltipTypeEnum.LABEL]: LabelTooltip,
  [TooltipTypeEnum.NO_FLY_ZONE]: NoFlyZoneTooltip,
  [TooltipTypeEnum.PERIMETER_PORTAL]: PerimeterPortalLabelTooltip,
  [TooltipTypeEnum.PERMANENT]: PermanentTooltips,
  [TooltipTypeEnum.POI]: PoiTooltip,
  [TooltipTypeEnum.STATISTIC]: StatisticPointTooltip,
  [TooltipTypeEnum.TARGET]: TargetTooltip,
} as const;

function getTooltipComponent<T extends TooltipTypeEnum>(type: T): FC<TooltipComponentProps<T>> {
  return tooltipComponents[type];
}

function Tooltip() {
  const { mousePosition, isMeasureActive } = useMapContext();
  const [position, featureToDisplay] = useMapTooltip(TOOLTIP_LAYERS);

  if (!featureToDisplay) {
    return null;
  }

  const Component = getTooltipComponent(featureToDisplay.type);

  return (
    <>
      {position && featureToDisplay.type !== TooltipTypeEnum.TARGET && (
        <Popup
          className={classNames('mapbox-popup', {
            hidden: isMeasureActive,
          })}
          offset={25}
          latitude={position.latitude}
          longitude={position.longitude}
          closeButton={false}
          closeOnClick={false}
        >
          <Component data={featureToDisplay.data} />
        </Popup>
      )}
      {position && featureToDisplay.type === TooltipTypeEnum.TARGET && <Component data={featureToDisplay.data} />}
      {mousePosition && (
        <Popup
          className={classNames('mapbox-popup', { hidden: !isMeasureActive })}
          offset={200}
          latitude={mousePosition.latitude ?? 0}
          longitude={mousePosition.longitude ?? 0}
          closeButton={false}
          closeOnClick={false}
          anchor="right"
        >
          {isMeasureActive && mousePosition.latitude != null && mousePosition.longitude != null && (
            <AzimuthAndDistanceTooltip data={{ tooltipPosition: mousePosition as Coordinates }} />
          )}
        </Popup>
      )}
    </>
  );
}

export default Tooltip;
